/**
 * Checkout, without the pixels.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 * Everything here is a pure function or a frozen string, so the rules that
 * decide whether a buyer can continue, what the procurement office receives,
 * and what P3 records as having been accepted are testable without a DOM.
 * `tests/checkout.test.ts` imports this module and nothing else. The page is
 * then free to be only a page.
 *
 * ── THE THREE THINGS THAT WERE WRONG BEFORE AND ARE FIXED HERE ────────────
 *  1. The quote request was posted as a NESTED object (`{institution: {...},
 *     billing: {...}}`). The Worker's route narrows the body to a FLAT list
 *     of names (`src/worker/routes/public.ts`, `reads.body`), so every field
 *     arrived undefined and the request failed validation on `legalName`
 *     before it reached the database. `quotePayload` builds the flat shape
 *     the Worker actually reads.
 *  2. Nothing the buyer typed outside that flat list survived the post. The
 *     billing address, the purchase order number, the fiscal year end and
 *     the two routing answers were all collected and then dropped on the
 *     floor. They are now composed into `notes`, which the Worker does store,
 *     under the 2,000 character ceiling its own parser applies.
 *  3. The Worker answers a quote with `{ quoteNumber, expiresAt }` and 202.
 *     The old page ignored both and told the buyer a generic sentence. The
 *     confirmation now shows the real quote number, because that is the
 *     string a purchasing office quotes back at you.
 *
 * ── THE CARD ORDER HAD THE SAME FAULT (Sep 14 2026) ───────────────────────
 * The card order nested the buyer's details under `institution`, the way the
 * quote once did. The session route narrows to flat names as well
 * (`CARD_ORDER_FIELDS` in src/worker/lib/checkout.ts), and the Worker's router
 * bans `institution` as an input name outright: it refuses to boot on it. So
 * `cardOrderPayload` builds the flat body, and tests/checkout.test.ts runs it
 * through the Worker's own parser rather than trusting a list of names.
 *
 * ── A NOTE ON THE BAND IDS ────────────────────────────────────────────────
 * Resolved Sep 9 2026. `src/worker/lib/rate-card.ts` used to carry its own
 * `band_250 / band_500 / band_1000` while this page sold `band_150 / band_300
 * / band_600 / band_1000 / quote`, and the Worker refused three of the five.
 * It now derives its bands from `src/app/lib/pricing.ts`, so there is one
 * list. `classifyFailure` still keeps a server-supplied message and the
 * failure banner prints it, because a refusal the buyer can read is one they
 * can act on.
 */

import type { Completion } from "~/worker/lib/stripe-events";
import { formatCount, formatDate, formatMoney } from "~/app/lib/format";
import {
  CANCELLATION_NOTICE_DAYS,
  INVOICE_NET_DAYS,
  LEGAL,
  QUOTE_RESPONSE_BUSINESS_DAYS,
  QUOTE_VALID_DAYS,
  RENEWAL_NOTICE_DAYS,
  SUPPRESSION_FLOOR,
  TERM_MONTHS,
  bandFromWire,
  parseStudentCount,
  type BandId,
  type CapacityBand,
} from "~/app/lib/pricing";
import { currentVersion, paneClauses, type DocumentClause } from "~/app/lib/legal";

/* ==========================================================================
   1. The shapes
   ========================================================================== */

export type CheckoutPath = "card" | "quote";

/** Which figure the buyer is charged on. The HERO figure is monthly either way. */
export type Billing = "monthly" | "annual";

export type StepId = "institution" | "payment" | "review";

/** A notice carried in on the query string when a buyer comes back to us. */
export type ReturnNotice = "cancelled" | "expired" | "declined" | null;

/**
 * Terminal and transient screens. `already` is its own phase rather than a
 * failure, because an institution that already subscribed has not done
 * anything wrong and must not be shown a red banner for it.
 */
export type Phase = "idle" | "submitting" | "sent" | "failed" | "already";

export type FailureKind =
  | "provider-unreachable"
  | "provider-refused"
  | "rate-limited"
  /** The Worker says the card path is closed: no key, a paused price list. */
  | "payments-closed"
  | "network";

export interface Institution {
  legalName: string;
  website: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  department: string;
}

export interface BillingAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  accountsPayableEmail: string;
}

export interface RoutingAnswers {
  publicInstitution: boolean | null;
}

/** Everything the three steps collect. One object, so validation is one call. */
export interface CheckoutState {
  path: CheckoutPath;
  billing: Billing;
  institution: Institution;
  address: BillingAddress;
  fiscalYearEnd: string;
  purchaseOrderNumber: string;
  notes: string;
  routing: RoutingAnswers;
  accepted: boolean;
}

export interface AcceptanceRecord {
  readonly termsVersion: string;
  readonly dsaVersion: string;
  readonly acceptedTerms: boolean;
  readonly acceptedDsa: boolean;
  readonly acceptedAt: string;
  readonly acceptingName: string;
  readonly acceptingTitle: string;
  readonly acceptingEmail: string;
  /** The exact sentence that sat beside the box, stored verbatim. */
  readonly statement: string;
  /** The exact renewal sentence as it was rendered on the screen. */
  readonly renewalDisclosure: string;
  /** Evidence, not a gate. True once the reader reached the end of the pane. */
  readonly readToEnd: { readonly terms: boolean; readonly addendum: boolean };
}

/**
 * What `POST /api/enterprise/checkout/session` accepts. Flat, like the quote,
 * because the Worker narrows to top-level names and bans `institution` as one.
 * The acceptance and the routing answers are the only nested values, and the
 * Worker reads both whole.
 */
export interface CardOrderPayload {
  readonly bandId: BandId;
  readonly billingInterval: "month" | "year";
  readonly estimatedStudents: number | null;
  readonly legalName: string;
  readonly website: string;
  readonly contactName: string;
  readonly contactTitle: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly department: string;
  readonly acceptance: AcceptanceRecord;
  readonly routing: RoutingAnswers;
}

/** What `POST /api/enterprise/quote` accepts. Flat, because the Worker narrows. */
export interface QuotePayload {
  readonly legalName: string;
  readonly displayName: string;
  readonly website: string;
  readonly band: BandId;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactTitle: string;
  readonly contactPhone: string;
  readonly apEmail: string;
  readonly department: string;
  readonly notes: string;
}

/**
 * What `POST /api/enterprise/checkout/lead` accepts: step 1 as typed, sent
 * when the buyer presses Continue (Sep 15 2026), so P3 can follow up on a
 * checkout that is never finished. Flat, like the other two. `leadId` is the
 * id the route answered the last time, so a second Continue updates that lead.
 */
export interface LeadPayload {
  readonly leadId: string | null;
  readonly bandId: BandId;
  readonly billingInterval: "month" | "year";
  readonly estimatedStudents: number | null;
  readonly path: CheckoutPath;
  readonly legalName: string;
  readonly website: string;
  readonly department: string;
  readonly contactName: string;
  readonly contactTitle: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
}

export interface QuoteAccepted {
  readonly quoteNumber: string;
  readonly expiresAt: number | null;
}

/* ==========================================================================
   2. The steps

   Three, and the buyer can see all three from the first screen. A flow whose
   length is visible is a flow a buyer will finish; Adobe shows the whole
   ladder and so does this.
   ========================================================================== */

export interface StepDescriptor {
  readonly id: StepId;
  readonly label: string;
  /** Four words at most. It sits under the label on the rail. */
  readonly hint: string;
}

export const CARD_STEPS: readonly StepDescriptor[] = [
  { id: "institution", label: "Your institution", hint: "Who is subscribing" },
  { id: "payment", label: "How you pay", hint: "Card or purchase order" },
  { id: "review", label: "Review and accept", hint: "Terms, then payment" },
];

export const QUOTE_STEPS: readonly StepDescriptor[] = [
  { id: "institution", label: "Your institution", hint: "Who is subscribing" },
  { id: "payment", label: "Where to send it", hint: "Billing and purchasing" },
  { id: "review", label: "Review and send", hint: "Check, then send" },
];

export function stepsFor(path: CheckoutPath): readonly StepDescriptor[] {
  return path === "card" ? CARD_STEPS : QUOTE_STEPS;
}

export const STEP_ORDER: readonly StepId[] = ["institution", "payment", "review"];

export function stepIndex(id: StepId): number {
  const at = STEP_ORDER.indexOf(id);
  return at === -1 ? 0 : at;
}

export function nextStep(id: StepId): StepId {
  return STEP_ORDER[Math.min(stepIndex(id) + 1, STEP_ORDER.length - 1)] ?? id;
}

export function previousStep(id: StepId): StepId {
  return STEP_ORDER[Math.max(stepIndex(id) - 1, 0)] ?? id;
}

/* ==========================================================================
   3. Validation

   Per step, so a buyer is never told about a field two screens away, and
   never blocked by one. Every message names the field and says what to do
   with it. None of them is "Invalid".
   ========================================================================== */

export type Errors = Readonly<Record<string, string>>;

export function validateStep(
  step: StepId,
  state: CheckoutState,
  options: { paymentsConnected: boolean; quotedBand: boolean },
): Errors {
  const errors: Record<string, string> = {};
  const { institution, address } = state;

  if (step === "institution") {
    if (!institution.legalName.trim()) {
      errors.legalName = "Enter the legal name your purchasing office uses.";
    }
    if (!institution.website.trim()) {
      errors.website = "Enter your institution's website.";
    }
    if (!institution.contactName.trim()) {
      errors.contactName = "Enter your full name.";
    }
    if (!institution.contactTitle.trim()) {
      errors.contactTitle = "Enter your title. It goes on the quote and on the record of who accepted.";
    }
    if (!isEmail(institution.contactEmail)) {
      errors.contactEmail = "Enter a work email address we can reply to.";
    }
  }

  if (step === "payment") {
    if (state.path === "card" && !options.paymentsConnected && !options.quotedBand) {
      errors.path = "Card payments are not connected yet. Choose the quote path to continue.";
    }
    if (state.path === "quote") {
      if (!address.line1.trim()) {
        errors.line1 = "Enter the billing address for the quote header.";
      }
      if (!address.city.trim()) errors.city = "Enter the city.";
      if (!address.state.trim()) errors.state = "Enter the state.";
      if (!address.postalCode.trim()) errors.postalCode = "Enter the ZIP code.";
      if (address.accountsPayableEmail.trim() && !isEmail(address.accountsPayableEmail)) {
        errors.accountsPayableEmail = "This is not an address we can send to. Correct it or clear it.";
      }
    }
  }

  if (step === "review" && state.path === "card") {
    if (!state.accepted) {
      errors.accepted =
        "Tick the box to accept the Terms and the Addendum. It is what makes the agreement real.";
    }
  }

  return errors;
}

/** Every step's errors at once, for the review screen's "go back and fix it". */
export function validateAll(
  state: CheckoutState,
  options: { paymentsConnected: boolean; quotedBand: boolean },
): Readonly<Record<StepId, Errors>> {
  return {
    institution: validateStep("institution", state, options),
    payment: validateStep("payment", state, options),
    review: validateStep("review", state, options),
  };
}

export function firstErrorKey(errors: Errors): string | null {
  const keys = Object.keys(errors);
  return keys.length === 0 ? null : (keys[0] ?? null);
}

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/**
 * A mismatch is a warning, never a block. A purchasing office often buys from
 * a shared address that has nothing to do with the campus domain.
 */
export function websiteDomainMismatch(website: string, email: string): string | null {
  const host = hostOf(website);
  const domain = email.includes("@") ? (email.split("@")[1] ?? "").toLowerCase() : "";
  if (!host || !domain) return null;
  if (domain === host || domain.endsWith(`.${host}`) || host.endsWith(`.${domain}`)) {
    return null;
  }
  return `This address is not on ${host}. It does not block anything. Verifying a domain later is what lets us offer your institution to students who sign up from it.`;
}

export function hostOf(website: string): string | null {
  const raw = website.trim().toLowerCase();
  if (!raw) return null;
  const stripped = raw
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
  return stripped && stripped.includes(".") ? stripped : null;
}

/* ==========================================================================
   4. What the procurement office receives

   `quotePayload` is the whole contract with the Worker. Field names here are
   the field names in `reads.body` on the quote route, spelled the same way,
   because anything else is silently dropped rather than rejected.
   ========================================================================== */

/** The Worker's own ceiling on the notes column. Truncation happens here, visibly. */
export const NOTES_LIMIT = 2000;

/** What the buyer may type. The composed block below is added on top. */
export const BUYER_NOTES_LIMIT = 500;

export function quotePayload(
  state: CheckoutState,
  band: CapacityBand,
  estimatedStudents: number | null,
): QuotePayload {
  const { institution, address } = state;
  return {
    legalName: institution.legalName.trim(),
    displayName: institution.legalName.trim(),
    website: institution.website.trim(),
    band: band.id,
    contactName: institution.contactName.trim(),
    contactEmail: institution.contactEmail.trim().toLowerCase(),
    contactTitle: institution.contactTitle.trim(),
    contactPhone: institution.contactPhone.trim(),
    apEmail: address.accountsPayableEmail.trim().toLowerCase(),
    department: institution.department.trim(),
    notes: composeNotes(state, band, estimatedStudents),
  };
}

/**
 * The card order, trimmed the way `buildAcceptance` trims, because the Worker
 * checks one against the other: the accepting name, title and email must
 * equal the contact fields, and the statement must be the one this module
 * builds for the trimmed legal name. A stray space would be refused as
 * ACCEPTANCE_MISMATCH on the last click.
 *
 * The program size is optional, and the Worker refuses one that is not a
 * positive whole number, so anything else is sent as not given.
 */
export function cardOrderPayload(
  state: CheckoutState,
  bandId: BandId,
  estimatedStudents: number | null,
  acceptance: AcceptanceRecord,
): CardOrderPayload {
  const { institution, routing } = state;
  return {
    bandId,
    billingInterval: state.billing === "annual" ? "year" : "month",
    estimatedStudents:
      estimatedStudents !== null && Number.isInteger(estimatedStudents) && estimatedStudents > 0
        ? estimatedStudents
        : null,
    legalName: institution.legalName.trim(),
    website: institution.website.trim(),
    contactName: institution.contactName.trim(),
    contactTitle: institution.contactTitle.trim(),
    contactEmail: institution.contactEmail.trim().toLowerCase(),
    contactPhone: institution.contactPhone.trim(),
    department: institution.department.trim(),
    acceptance,
    routing: { publicInstitution: routing.publicInstitution },
  };
}

/**
 * Step 1, trimmed and lowercased the way the card order is, so the lead and
 * the order it becomes carry the same address and the follow-up can tell
 * that one finished.
 */
export function leadPayload(
  state: CheckoutState,
  bandId: BandId,
  path: CheckoutPath,
  estimatedStudents: number | null,
  leadId: string | null,
): LeadPayload {
  const { institution } = state;
  return {
    leadId,
    bandId,
    billingInterval: state.billing === "annual" ? "year" : "month",
    estimatedStudents:
      estimatedStudents !== null && Number.isInteger(estimatedStudents) && estimatedStudents > 0
        ? estimatedStudents
        : null,
    path,
    legalName: institution.legalName.trim(),
    website: institution.website.trim(),
    department: institution.department.trim(),
    contactName: institution.contactName.trim(),
    contactTitle: institution.contactTitle.trim(),
    contactEmail: institution.contactEmail.trim().toLowerCase(),
    contactPhone: institution.contactPhone.trim(),
  };
}

/** The line under step 1's Continue. True because that click is the one that keeps the details. */
export const LEAD_NOTE =
  "P3 saves these details when you continue, so we can follow up by email if you do not finish.";

/**
 * The quote's working notes. Everything the buyer told us that the Worker has
 * no column for goes here in a fixed order, so a person reading the record
 * sees the same facts in the same places on every request.
 */
export function composeNotes(
  state: CheckoutState,
  band: CapacityBand,
  estimatedStudents: number | null,
): string {
  const lines: string[] = [];

  if (state.notes.trim()) {
    lines.push(`From the requester: ${state.notes.trim()}`);
    lines.push("");
  }

  lines.push(`Capacity commitment: ${band.name}.`);
  lines.push(
    band.monthly === null || band.annual === null
      ? "Price: quoted on the cohort."
      : `Price: ${formatMoney(band.monthly)} a month, or ${formatMoney(band.annual)} a year, ${lowerWords(describeSaving(band))}.`,
  );
  lines.push(
    `Billing the requester chose: ${state.billing === "annual" ? "annual, one payment" : "monthly"}.`,
  );
  if (estimatedStudents !== null) {
    lines.push(`Program size the requester gave: ${formatCount(estimatedStudents)} students.`);
  }

  const billTo = [
    state.address.line1.trim(),
    state.address.line2.trim(),
    [state.address.city.trim(), state.address.state.trim()].filter(Boolean).join(", "),
    state.address.postalCode.trim(),
  ].filter(Boolean);
  if (billTo.length > 0) {
    lines.push("");
    lines.push(`Bill to: ${billTo.join(" / ")}.`);
  }

  if (state.purchaseOrderNumber.trim()) {
    lines.push(`Purchase order already raised: ${state.purchaseOrderNumber.trim()}.`);
  }
  if (state.fiscalYearEnd.trim()) {
    lines.push(`Fiscal year end: ${state.fiscalYearEnd.trim()}. Start the term to suit it.`);
  }

  lines.push("");
  lines.push(`Public institution: ${answerWord(state.routing.publicInstitution)}.`);
  if (state.routing.publicInstitution === true) {
    lines.push("Route to a signed agreement if state law bars a click-through acceptance.");
  }

  return truncate(lines.join("\n"), NOTES_LIMIT);
}

function answerWord(value: boolean | null): string {
  return value === null ? "not answered" : value ? "yes" : "no";
}

function lowerWords(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export function truncate(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;
}

/* ==========================================================================
   5. The sentences that are recorded, built from the same values that render

   A record of acceptance is worth what its wording is worth. These are the
   only place the sentences exist, so what P3 stores is the string the buyer
   read, not a paraphrase written months later by whoever wrote the emailer.
   ========================================================================== */

/** The saving as an amount a reader can check against the two figures beside
    it, rather than as "two months free", which they cannot. Same change made
    on the pricing slider Sep 8 2026; the two surfaces must not disagree. */
export function describeSaving(band: CapacityBand): string {
  if (band.monthly === null || band.annual === null) return "";
  return `${formatMoney(band.monthly * 12 - band.annual)} less than paying monthly`;
}

export function renewalSentence(
  band: CapacityBand,
  billing: Billing,
  dates: TermDates,
): string {
  if (band.monthly === null || band.annual === null) return "";
  // A MONTHLY plan has no twelve-month term (Thomas, Sep 15 2026): it renews
  // each month and, once renewal is off, bills through the next full month.
  // No dates, so the Worker checks this sentence exactly.
  if (billing === "monthly") {
    return `This subscription renews each month at ${formatMoney(band.monthly)} a month until you turn renewal off, and then ends at the end of the next full month. You can turn renewal off at any time in your dashboard under Billing, or by writing to ${LEGAL.contactEmail}.`;
  }
  const charge = `${formatMoney(band.annual)} a year`;
  // Both dates are formatted here, not interpolated. A Date dropped into a
  // template literal renders as "Wed Sep 08 2027 10:37:09 GMT-0500 (Central
  // Daylight Time)", and this sentence is not only rendered: it is STORED on
  // the acceptance record as the disclosure the buyer was shown.
  const renewal = formatDate(dates.renewal, { style: "long" });
  const deadline = formatDate(dates.deadline, { style: "long" });
  return `This subscription renews on ${renewal} for a further ${TERM_MONTHS} months at ${charge}, unless you tell us at least ${CANCELLATION_NOTICE_DAYS} days before that date, which is ${deadline}. You can turn renewal off at any time in your dashboard under Billing, or by writing to ${LEGAL.contactEmail}.`;
}

export function acceptanceStatement(legalName: string): string {
  const who = legalName.trim() || "my institution";
  return `I have read the Enterprise Subscription Terms ${LEGAL.termsVersion} and the Data Sharing Addendum ${LEGAL.dsaVersion} shown above, I agree to both on behalf of ${who}, and I am authorized to enter into this agreement for it.`;
}

export function buildAcceptance(args: {
  institution: Institution;
  statement: string;
  renewalDisclosure: string;
  readToEnd: { terms: boolean; addendum: boolean };
  at?: Date;
}): AcceptanceRecord {
  return {
    termsVersion: LEGAL.termsVersion,
    dsaVersion: LEGAL.dsaVersion,
    acceptedTerms: true,
    acceptedDsa: true,
    acceptedAt: (args.at ?? new Date()).toISOString(),
    acceptingName: args.institution.contactName.trim(),
    acceptingTitle: args.institution.contactTitle.trim(),
    acceptingEmail: args.institution.contactEmail.trim().toLowerCase(),
    statement: args.statement,
    renewalDisclosure: args.renewalDisclosure,
    readToEnd: { terms: args.readToEnd.terms, addendum: args.readToEnd.addendum },
  };
}

/* ==========================================================================
   6. Dates
   ========================================================================== */

export interface TermDates {
  readonly renewal: Date;
  readonly deadline: Date;
  readonly quoteValidUntil: Date;
}

export function termDates(now: Date = new Date()): TermDates {
  const renewal = new Date(now);
  renewal.setMonth(renewal.getMonth() + TERM_MONTHS);
  const deadline = new Date(renewal);
  deadline.setDate(deadline.getDate() - CANCELLATION_NOTICE_DAYS);
  const quoteValidUntil = new Date(now);
  quoteValidUntil.setDate(quoteValidUntil.getDate() + QUOTE_VALID_DAYS);
  return { renewal, deadline, quoteValidUntil };
}

/* ==========================================================================
   7. The query string

   Pure, so a test can drive it without a location. The page hands it
   `window.location.search` when there is a window and an empty string when
   there is not.
   ========================================================================== */

export interface CheckoutQuery {
  readonly path: CheckoutPath | null;
  readonly bandId: BandId | null;
  readonly students: number | null;
  readonly billing: Billing | null;
  readonly notice: ReturnNotice;
}

export function readCheckoutQuery(search: string): CheckoutQuery {
  const params = new URLSearchParams(search);
  const rawPath = params.get("path");
  const rawBilling = params.get("billing");
  const rawStudents = params.get("students");
  const rawNotice = params.get("notice");
  return {
    path: rawPath === "card" || rawPath === "quote" ? rawPath : null,
    bandId: bandFromWire(params.get("band"))?.id ?? null,
    students: rawStudents === null ? null : parseStudentCount(rawStudents),
    billing: rawBilling === "monthly" || rawBilling === "annual" ? rawBilling : null,
    notice:
      rawNotice === "cancelled" || rawNotice === "expired" || rawNotice === "declined"
        ? rawNotice
        : null,
  };
}

/* ==========================================================================
   8. Failure

   The Worker's error body is `{ error: { code, message } }` and its own
   comment says `message` is safe to show. So we show it. A generic "something
   went wrong" over the top of a server that just said "That is more quote
   requests than we accept in an hour" is a worse page, not a safer one.
   ========================================================================== */

export class ProviderError extends Error {
  readonly status: number;
  /** The Worker's own message, when it sent one. */
  readonly serverMessage: string | null;
  readonly code: string | null;

  constructor(status: number, serverMessage: string | null, code: string | null) {
    super(`provider responded ${status}`);
    this.name = "ProviderError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.code = code;
  }
}

/** 409 is not a failure. It is an institution that already bought this. */
export class AlreadySubscribedError extends Error {
  readonly serverMessage: string | null;
  constructor(serverMessage: string | null) {
    super("already subscribed");
    this.name = "AlreadySubscribedError";
    this.serverMessage = serverMessage;
  }
}

/**
 * The Worker's two ways of saying the card path is closed (Sep 14 2026):
 * PAYMENTS_UNCONFIGURED for a deployment that cannot take a card at all, and
 * PRICE_UNAVAILABLE while a plan's Stripe Price disagrees with the rate card.
 */
const CARD_CLOSED_CODES: ReadonlySet<string> = new Set(["PAYMENTS_UNCONFIGURED", "PRICE_UNAVAILABLE"]);

export function classifyFailure(error: unknown): FailureKind {
  if (error instanceof ProviderError) {
    // The code before the status. Both closed codes arrive as 503, and a 503
    // read by its status alone says "try again in a moment" about a door that
    // stays shut however often the buyer knocks.
    if (error.code !== null && CARD_CLOSED_CODES.has(error.code)) return "payments-closed";
    if (error.status === 429) return "rate-limited";
    return error.status >= 500 ? "provider-unreachable" : "provider-refused";
  }
  return "network";
}

export function serverMessageOf(error: unknown): string | null {
  if (error instanceof ProviderError) return error.serverMessage;
  if (error instanceof AlreadySubscribedError) return error.serverMessage;
  return null;
}

export interface FailureCopy {
  readonly title: string;
  readonly body: string;
  /** Offer the quote path out of it. False where the quote path is the one failing. */
  readonly offerQuote: boolean;
  readonly offerRetry: boolean;
}

export const FAILURE_COPY: Readonly<Record<FailureKind, FailureCopy>> = {
  "provider-unreachable": {
    title: "We could not reach our own server",
    body: "Nothing was charged and nothing you typed has been lost. Try again in a moment.",
    offerQuote: true,
    offerRetry: true,
  },
  "provider-refused": {
    title: "Our server would not accept that",
    body: "Nothing was charged. This is on our side, not yours.",
    offerQuote: true,
    offerRetry: true,
  },
  "rate-limited": {
    title: "That is more requests than we accept in an hour",
    body: `Nothing was charged. Write to ${LEGAL.contactEmail} and a person will pick it up.`,
    offerQuote: false,
    offerRetry: false,
  },
  "payments-closed": {
    title: "Card payments are not open yet",
    body: "Nothing was charged. Take a quote instead, and we will invoice your purchasing office.",
    offerQuote: true,
    offerRetry: false,
  },
  network: {
    title: "That did not reach us",
    // Not "nothing was sent" (Sep 15 2026): step 1 is kept when the buyer
    // continues, so the order is what did not arrive, not everything.
    body: "Your connection dropped before we received your order, so nothing was charged. Everything you typed is still here.",
    offerQuote: true,
    offerRetry: true,
  },
};

/**
 * What the failure banner says: the Worker's own sentence when it sent one,
 * then ours under it, unless the Worker's already contains ours word for
 * word. Both closed-path messages end on the payments-closed body exactly,
 * and a banner that says "Take a quote instead" twice reads as a glitch.
 */
export function failureLines(
  kind: FailureKind,
  serverSaid: string | null,
): { readonly lead: string; readonly hint: string | null } {
  const { body } = FAILURE_COPY[kind];
  if (serverSaid === null) return { lead: body, hint: null };
  return { lead: serverSaid, hint: serverSaid.includes(body) ? null : body };
}

/* `body` is only true when the buyer's details came back with them (section 13
   keeps them across the Stripe round trip); `bodyNotKept` is what the banner
   says when this tab had nothing to restore, e.g. the address was opened in a
   new tab. `noticeBody` picks, so the banner never promises details the form
   does not hold (Sep 25 2026, accessibility audit E20). */
export const NOTICE_COPY: Readonly<
  Record<Exclude<ReturnNotice, null>, { title: string; body: string; bodyNotKept: string }>
> = {
  cancelled: {
    title: "No payment was taken",
    body: "You came back before paying. Your details are still here, exactly as you left them.",
    bodyNotKept: "You came back before paying. This browser did not keep your details, so enter them again to continue.",
  },
  expired: {
    title: "That payment link expired",
    body: "Nothing was charged. Your details are still here. Check your order below and continue when you are ready.",
    bodyNotKept: "Nothing was charged. This browser did not keep your details, so enter them again, then continue.",
  },
  declined: {
    title: "Your bank declined the card",
    body: "Nothing was charged. Your details are still here. Try a different card, or switch to a quote and we will invoice your purchasing office.",
    bodyNotKept: "Nothing was charged. Enter your details again, then try a different card, or switch to a quote and we will invoice your purchasing office.",
  },
};

export function noticeBody(notice: Exclude<ReturnNotice, null>, kept: boolean): string {
  return kept ? NOTICE_COPY[notice].body : NOTICE_COPY[notice].bodyNotKept;
}

/* ==========================================================================
   9. The two paths, described in the buyer's terms

   The quote path is not the fallback. A university buyer often cannot use a
   card at all, and a page that treats a purchase order as an apology loses
   the sale to a page that does not.
   ========================================================================== */

export interface PathOption {
  readonly id: CheckoutPath;
  readonly title: string;
  /** At most twelve words. */
  readonly body: string;
  readonly note: string;
}

export const PATH_OPTIONS: readonly PathOption[] = [
  {
    id: "card",
    title: "Pay by card",
    body: "By card, through Stripe.",
    note: "Your dashboard is ready the moment payment confirms.",
  },
  {
    id: "quote",
    title: "Quote and purchase order",
    body: "A PDF quote, then an invoice.",
    note: `Net ${INVOICE_NET_DAYS} terms. Your dashboard turns on the day we invoice.`,
  },
];

/* The second line promised the join link, code and QR "straight away" until
   Sep 14 2026. A card tenant starts without a code: P3 connects it to the
   student app by hand within one business day (Thomas: one click in Retool),
   and the Worker's welcome email says the same. */
export const CARD_NEXT: readonly string[] = [
  "Stripe collects the payment. P3 never sees your card number.",
  "Your workspace opens straight away. Your join link, six character code and QR code follow within one business day, once P3 connects your institution to the student app.",
  `Reporting starts once ${SUPPRESSION_FLOOR} students have claimed you.`,
];

export const QUOTE_NEXT: readonly string[] = [
  `A PDF quote with a quote number, good for ${QUOTE_VALID_DAYS} days, plus our W-9 and our ACH, wire and check details.`,
  "Your purchasing office raises a purchase order against it.",
  `Send us the purchase order number and we invoice on Net ${INVOICE_NET_DAYS} terms. Your dashboard turns on the day we send the invoice.`,
  "The Terms and the Addendum are accepted at that step, by a named person, and we keep the record.",
];

/** What a quote request costs the buyer, said once, in the buyer's words. */
export const QUOTE_COMMITS_NOTHING = `Asking for a quote commits nothing. We reply in writing within ${QUOTE_RESPONSE_BUSINESS_DAYS} business day.`;

/* --------------------------------------------------------------------------
   THE LAST BUTTON, AND THE SENTENCE THAT NAMES IT

   Every other call to action on this surface now says "Register". This one
   still says "Start subscription", and it stays that way until Thomas rules
   on it, because renaming the moment of commitment is a product decision
   rather than a defect fix: "Clicking Register enters your institution into a
   binding agreement" undersells what the click does, and a buyer who has just
   read two documents deserves a verb that matches them.

   WHAT IS FIXED HERE IS THE DRIFT, NOT THE WORDING. The label lived in a
   function in Checkout.tsx and the consequence sentence beside it was a
   hand-typed string containing the same words. Change one and the page tells
   the buyer to click a button that is not on it. Both now read the same
   constant, so whichever way Thomas rules, ONE edit moves both and they
   cannot disagree. `tests/checkout.test.ts` asserts the sentence contains the
   label, so a future edit that separates them fails rather than ships.
   -------------------------------------------------------------------------- */

/** The resting label on the final submit, per path. */
export const SUBMIT_LABEL: Readonly<Record<CheckoutPath, string>> = {
  card: "Start subscription",
  quote: "Send me a quote",
};

/** The same button while the request is in flight. */
export const SUBMIT_LABEL_BUSY: Readonly<Record<CheckoutPath, string>> = {
  card: "Opening payment",
  quote: "Sending your request",
};

/**
 * What clicking it does, in a sentence that NAMES the button by reading the
 * same constant the button reads.
 */
export function commitmentSentence(path: CheckoutPath): string {
  return `Clicking ${SUBMIT_LABEL[path]} enters your institution into a binding agreement.`;
}

/* ==========================================================================
   10. The documents, displayed rather than linked

   A click-through is enforceable when the person clicking had a real chance
   to read what they were agreeing to. The Terms clauses come from
   `KEY_TERMS`. The Addendum excerpt is PROJECTED from the canonical document
   in src/app/lib/legal.ts (sections I, II, III, V, VII, X and XII), which is
   the same document the Worker serves at the permanent versioned address the
   pane links to. Nothing in this block is typed here, so the excerpt cannot
   drift from the document it excerpts. tests/legal.test.ts holds the two
   equal.
   ========================================================================== */

export type { DocumentClause } from "~/app/lib/legal";

export const DSA_CLAUSES: readonly DocumentClause[] = paneClauses(currentVersion("data-sharing-addendum"));

/** What the two tabs are called, and what each one is. */
export const DOCUMENT_TABS = [
  {
    id: "terms" as const,
    label: `Subscription Terms ${LEGAL.termsVersion}`,
    href: LEGAL.termsUrl,
  },
  {
    id: "addendum" as const,
    label: `Data Sharing Addendum ${LEGAL.dsaVersion}`,
    href: LEGAL.dsaUrl,
  },
];

export type DocumentTabId = (typeof DOCUMENT_TABS)[number]["id"];

/* ==========================================================================
   11. Empties
   ========================================================================== */

export const EMPTY_INSTITUTION: Institution = {
  legalName: "",
  website: "",
  contactName: "",
  contactTitle: "",
  contactEmail: "",
  contactPhone: "",
  department: "",
};

export const EMPTY_ADDRESS: BillingAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  accountsPayableEmail: "",
};

export function emptyState(path: CheckoutPath, billing: Billing): CheckoutState {
  return {
    path,
    billing,
    institution: { ...EMPTY_INSTITUTION },
    address: { ...EMPTY_ADDRESS },
    fiscalYearEnd: "",
    purchaseOrderNumber: "",
    notes: "",
    routing: { publicInstitution: null },
    accepted: false,
  };
}

/** The renewal reminder schedule, said once. Used on the review step. */
export const RENEWAL_REMINDERS = `We email your administrators ${RENEWAL_NOTICE_DAYS[0]} days and ${RENEWAL_NOTICE_DAYS[1]} days before the renewal date. There is no automatic price increase.`;

/* ==========================================================================
   12. Whether the card path is open (Sep 14 2026)

   The Worker decides, per deployment, at GET /api/enterprise/checkout/
   availability. Until then a build-time Stripe publishable key decided, and a
   key baked into a bundle cannot know whether the deployment holds a secret
   key, a webhook secret, the test rehearsal switch or published documents.
   Hosted Checkout needs no publishable key at all.

   Closed is the default: before the answer arrives, when the route cannot be
   reached, and when its body is not the shape it promised. The worst a slow
   or broken answer can do is show the designed "not connected yet" panel and
   the quote path, never a card form that cannot work.
   ========================================================================== */

export interface CardAvailability {
  readonly card: boolean;
  readonly mode: "live" | "test" | null;
}

export const CARD_CLOSED: CardAvailability = { card: false, mode: null };

/** An open answer must name its mode, or the test-mode banner could silently not show. */
export function readAvailability(json: unknown): CardAvailability {
  if (!isRecord(json) || json["card"] !== true) return CARD_CLOSED;
  const mode = json["mode"];
  return mode === "live" || mode === "test" ? { card: true, mode } : CARD_CLOSED;
}

/** Said on the card path, and on the completion page, whenever Stripe is in test mode. */
export const TEST_MODE_NOTICE = {
  title: "Stripe test mode",
  body: "This checkout runs against Stripe's test environment. No real card is charged.",
} as const;

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/* ==========================================================================
   13. Coming back from Stripe (Sep 14 2026)

   Stripe returns a buyer who paid to /checkout/complete?session_id=<id>, and
   CheckoutComplete.tsx asks GET /api/enterprise/checkout/complete what to
   show. The answer is typed where the Worker builds it (`Completion` in
   src/worker/lib/stripe-events.ts) and read here field by field, because a
   page that trusts a body's shape renders "undefined is registered." the day
   that shape changes.
   ========================================================================== */

export type CheckoutCompletion = Completion;

export type JoinKit = NonNullable<Extract<CheckoutCompletion, { state: "ready" }>["join"]>;

/** The id shape the Worker accepts. Anything else is not sent, and reads as unknown. */
const SESSION_ID = /^[A-Za-z0-9_]{8,255}$/;

export function readSessionId(search: string): string | null {
  const value = new URLSearchParams(search).get("session_id");
  return value !== null && SESSION_ID.test(value) ? value : null;
}

function isWebAddress(value: unknown): value is string {
  return typeof value === "string" && /^https?:\/\/\S+$/i.test(value);
}

export function readCompletion(json: unknown): CheckoutCompletion | null {
  if (!isRecord(json)) return null;
  const state = json["state"];
  if (state === "unknown") return { state };
  const livemode = json["livemode"];
  if (typeof livemode !== "boolean") return null;
  if (state === "unpaid" || state === "expired" || state === "preparing" || state === "attention") {
    return { state, livemode };
  }
  if (state !== "ready") return null;
  const institution = json["institution"];
  const loginUrl = json["loginUrl"];
  if (typeof institution !== "string" || institution.trim() === "" || !isWebAddress(loginUrl)) return null;
  // A join kit that does not read is shown as not issued yet, rather than
  // hiding the dashboard from a buyer who has paid.
  return { state, livemode, institution: institution.trim(), loginUrl, join: readJoinKit(json["join"]) };
}

function readJoinKit(value: unknown): JoinKit | null {
  if (!isRecord(value)) return null;
  const code = value["code"];
  const joinUrl = value["joinUrl"];
  const posterUrl = value["posterUrl"];
  return typeof code === "string" && code.trim() !== "" && isWebAddress(joinUrl) && isWebAddress(posterUrl)
    ? { code: code.trim(), joinUrl, posterUrl }
    : null;
}

export type CompletionRead =
  | { readonly kind: "read"; readonly completion: CheckoutCompletion }
  | { readonly kind: "failed" };

/**
 * What one read of the completion route means. A 400 is the Worker calling
 * the id malformed, which a buyer reads the same way as an id it never
 * opened. Anything else that is not a readable 200 is a FAILED read, never
 * "unknown": a dropped connection must not tell someone who just paid that
 * their checkout does not exist.
 */
export function completionFromResponse(status: number | null, json: unknown): CompletionRead {
  if (status === 400) return { kind: "read", completion: { state: "unknown" } };
  if (status !== 200) return { kind: "failed" };
  const completion = readCompletion(json);
  return completion === null ? { kind: "failed" } : { kind: "read", completion };
}

/* ---- the two waits ------------------------------------------------------ */

export const COMPLETION_POLL = {
  /** `preparing`: Stripe could not be reached to confirm yet. Soon, and not forever. */
  preparingEveryMs: 3_000,
  preparingTries: 20,
  /** `ready` without a join kit: P3 connects it by hand, so ask while the page stays open. */
  joinEveryMs: 30_000,
  joinWindowMs: 10 * 60_000,
} as const;

/**
 * Milliseconds until the page asks again, or null to stop. `preparingTries`
 * counts the reads, this one included, that have answered `preparing` in a
 * row; `joinWaitedMs` is the time since the page first showed a workspace
 * without a join kit.
 */
export function nextCompletionPoll(
  completion: CheckoutCompletion,
  progress: { readonly preparingTries: number; readonly joinWaitedMs: number },
): number | null {
  if (completion.state === "preparing") {
    return progress.preparingTries < COMPLETION_POLL.preparingTries ? COMPLETION_POLL.preparingEveryMs : null;
  }
  if (completion.state === "ready" && completion.join === null) {
    return progress.joinWaitedMs < COMPLETION_POLL.joinWindowMs ? COMPLETION_POLL.joinEveryMs : null;
  }
  return null;
}

/* ---- what it says ------------------------------------------------------- */

export function registeredHeadline(institution: string): string {
  return `${institution.trim() || "Your institution"} is registered.`;
}

export const COMPLETION_COPY = {
  label: "Registration",
  loading: "Checking your checkout.",
  ready: {
    label: "Registration complete",
    joinLede: "Your students join with this code or this link.",
    codeLabel: "Your join code",
    joinPending:
      "Your join link, six character code and QR code arrive within one business day, once P3 connects your institution to the student app. We email you when they are ready.",
    dashboard: "Open your dashboard",
    signIn: "When you sign in, a code arrives by email at the work address you used at checkout.",
  },
  preparing: {
    title: "We are setting up your workspace.",
    body: "This page updates on its own.",
    stillBody: "We are still setting up your workspace. We email you when it is ready.",
  },
  unpaid: {
    title: "This checkout has not been paid.",
    action: "Back to checkout",
  },
  expired: {
    title: "That checkout expired before payment.",
    body: "Nothing was charged.",
    action: "Back to pricing",
  },
  attention: {
    title: "Your checkout needs a person at P3.",
    body: "We have been told and will write to you within one business day.",
  },
  unknown: {
    title: "We could not find that checkout.",
    signIn: "Sign in",
    pricing: "Back to pricing",
  },
  unreachable: {
    title: "This page could not load your checkout.",
    body: "Try again in a moment. Loading it again changes nothing about your payment.",
    action: "Try again",
  },
} as const;

/* ==========================================================================
   14. The buyer's details survive the trip to Stripe (Sep 25 2026)

   Hosted Checkout is a full-page redirect, and the address a buyer comes back
   to carries only the band, the billing choice and the student estimate. The
   cancelled notice promised "Your details are still here, exactly as you left
   them" while the page started empty (accessibility audit E20, WCAG 3.3.7
   Redundant Entry). So the page keeps what the buyer typed in this tab's
   session storage just before it leaves for Stripe, and puts it back when they
   return with a notice. Session storage lives and dies with the tab, and a
   successful payment clears it (CheckoutComplete).

   The acceptance is never kept: a buyer who comes back ticks it again. The
   lead id is kept, so Continue on step 1 updates the same lead instead of
   opening a second one. A draft is read field by field against the empty
   state, so a stale or tampered value can only ever fill a field, never add
   one, and anything older than two days is ignored.
   ========================================================================== */

export const CHECKOUT_DRAFT_KEY = "p3ent-checkout-draft";
export const CHECKOUT_DRAFT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

/** The part of `Storage` a draft needs, so tests can pass a plain object. */
export interface DraftStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CheckoutDraft {
  readonly state: CheckoutState;
  readonly leadId: string | null;
}

export function saveCheckoutDraft(
  store: DraftStore | null,
  state: CheckoutState,
  leadId: string | null,
  now: number = Date.now(),
): boolean {
  if (!store) return false;
  const { accepted: _accepted, ...kept } = state;
  try {
    store.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify({ savedAt: now, leadId, state: kept }));
    return true;
  } catch {
    return false;
  }
}

export function clearCheckoutDraft(store: DraftStore | null): void {
  try {
    store?.removeItem(CHECKOUT_DRAFT_KEY);
  } catch {
    /* storage refused: nothing was kept to clear */
  }
}

const text = (value: unknown, fallback: string): string => (typeof value === "string" ? value : fallback);

function overlay<T extends object>(empty: T, raw: unknown): T {
  const source = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out = { ...empty } as Record<string, unknown>;
  for (const key of Object.keys(empty)) out[key] = text(source[key], (empty as Record<string, string>)[key] ?? "");
  return out as T;
}

export function readCheckoutDraft(store: DraftStore | null, now: number = Date.now()): CheckoutDraft | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(CHECKOUT_DRAFT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: { savedAt?: unknown; leadId?: unknown; state?: unknown };
  try {
    parsed = JSON.parse(raw) as typeof parsed;
  } catch {
    return null;
  }
  if (typeof parsed.savedAt !== "number" || now - parsed.savedAt > CHECKOUT_DRAFT_MAX_AGE_MS || parsed.savedAt > now + 60_000) {
    return null;
  }
  const saved = (parsed.state && typeof parsed.state === "object" ? parsed.state : null) as Record<string, unknown> | null;
  if (!saved) return null;
  const path: CheckoutPath = saved.path === "quote" ? "quote" : "card";
  const billing: Billing = saved.billing === "annual" ? "annual" : "monthly";
  const base = emptyState(path, billing);
  const routing = (saved.routing && typeof saved.routing === "object" ? saved.routing : {}) as Record<string, unknown>;
  const state: CheckoutState = {
    ...base,
    institution: overlay(EMPTY_INSTITUTION, saved.institution),
    address: overlay(EMPTY_ADDRESS, saved.address),
    fiscalYearEnd: text(saved.fiscalYearEnd, ""),
    purchaseOrderNumber: text(saved.purchaseOrderNumber, ""),
    notes: text(saved.notes, ""),
    routing: { publicInstitution: typeof routing.publicInstitution === "boolean" ? routing.publicInstitution : null },
    accepted: false,
  };
  return { state, leadId: typeof parsed.leadId === "string" && parsed.leadId ? parsed.leadId : null };
}
